import { useState } from 'react';
import { ShieldCheck, PlayCircle, Save } from 'lucide-react';
import { adminAxios } from '../../pages/admin/AdminLayout';

export const COMPLIANCE_STATUSES = ['NOT_REQUIRED', 'REVIEW_REQUIRED', 'IN_REVIEW', 'CLEARED', 'CLEARED_WITH_CONDITIONS', 'LICENCE_REQUIRED', 'DECLINED', 'ON_HOLD'];
var DECISION_STATUSES = ['CLEARED', 'CLEARED_WITH_CONDITIONS', 'LICENCE_REQUIRED', 'DECLINED'];

export var STATUS_STYLE = {
  NOT_REQUIRED: 'bg-slate-100 text-slate-500',
  REVIEW_REQUIRED: 'bg-amber-100 text-amber-800',
  IN_REVIEW: 'bg-blue-100 text-blue-700',
  CLEARED: 'bg-green-100 text-green-700',
  CLEARED_WITH_CONDITIONS: 'bg-emerald-100 text-emerald-800',
  LICENCE_REQUIRED: 'bg-purple-100 text-purple-700',
  DECLINED: 'bg-red-100 text-red-700',
  ON_HOLD: 'bg-orange-100 text-orange-700',
};

export function ComplianceBadge(props) {
  var s = props.status || 'NOT_REQUIRED';
  return <span className={'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide ' + (STATUS_STYLE[s] || STATUS_STYLE.NOT_REQUIRED)} data-testid={props.testId || 'compliance-badge'}><ShieldCheck className="w-3 h-3" />{s.replace(/_/g, ' ')}</span>;
}

var FIELD_LABELS = [
  ['company_legal_name', 'Company legal name'], ['country_of_incorporation', 'Country of incorporation'], ['hq_country', 'Headquarters country'],
  ['ultimate_parent_name', 'Ultimate parent'], ['ultimate_parent_hq_country', 'Ultimate parent HQ'], ['delivery_destination', 'Delivery destination'],
  ['installation_country', 'Installation country'], ['buyer_is_end_user', 'Buyer is end user'], ['end_user_name', 'End-user name'],
  ['end_user_country', 'End-user country'], ['intended_use', 'Intended use'], ['reexport_intended', 'Re-export / resale'],
];

function fmt(v) {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return v || '—';
}

function Section(props) {
  return <div className="mb-3"><p className="font-manrope text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">{props.title}</p><div className="grid grid-cols-2 gap-x-4 gap-y-1">{props.children}</div></div>;
}
function Row(props) {
  return <div className="font-manrope text-xs"><span className="text-slate-500">{props.k}: </span><span className="text-slate-900">{fmt(props.v)}</span></div>;
}

// Shared review panel for quotes and website enquiries. Transaction-level only — never touches recipients.
export function ComplianceReviewPanel(props) {
  var data = props.data || {};
  var source = props.source || data.source || 'quote';
  var caseId = props.caseId || data.id;
  var status = data.compliance_status || 'NOT_REQUIRED';
  var c = data.compliance || {};
  var file = data.compliance_case_file;
  var log = data.compliance_log || [];

  var [decision, setDecision] = useState('');
  var [note, setNote] = useState('');
  var [conditions, setConditions] = useState(data.compliance_conditions || '');
  var [licenceRef, setLicenceRef] = useState(data.compliance_licence_reference || '');
  var [eccn, setEccn] = useState(data.compliance_eccn || '');
  var [busy, setBusy] = useState(false);
  var [error, setError] = useState('');

  if (status === 'NOT_REQUIRED' && !props.forceShow) return null;

  function fail(err) {
    var d = err && err.response && err.response.data && err.response.data.detail;
    setError(typeof d === 'string' ? d : 'Update failed');
  }

  async function startReview() {
    setBusy(true); setError('');
    try { await adminAxios().post('/api/admin/compliance/cases/' + source + '/' + caseId + '/start-review'); if (props.onChanged) props.onChanged(); }
    catch (err) { fail(err); } finally { setBusy(false); }
  }

  async function recordDecision() {
    if (!decision) { setError('Select a compliance status first'); return; }
    if (DECISION_STATUSES.indexOf(decision) >= 0 && !note.trim()) { setError('A reviewer note is required for ' + decision.replace(/_/g, ' ')); return; }
    setBusy(true); setError('');
    try {
      await adminAxios().put('/api/admin/compliance/cases/' + source + '/' + caseId, { status: decision, note: note, conditions: conditions, licence_reference: licenceRef, eccn: eccn });
      setDecision(''); setNote('');
      if (props.onChanged) props.onChanged();
    } catch (err) { fail(err); } finally { setBusy(false); }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4" data-testid={'compliance-panel-' + caseId}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <p className="font-manrope text-xs font-semibold text-slate-700 uppercase tracking-wide">Export Compliance</p>
          <ComplianceBadge status={status} testId={'compliance-status-' + caseId} />
        </div>
        {(status === 'REVIEW_REQUIRED' || status === 'ON_HOLD') && (
          <button onClick={startReview} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0096D6] text-white font-manrope text-xs font-semibold disabled:opacity-60" data-testid={'compliance-start-review-' + caseId}>
            <PlayCircle className="w-3.5 h-3.5" /> Start Compliance Review
          </button>
        )}
      </div>

      {file ? (
        <div className="bg-white rounded-lg border border-slate-200 p-3 mb-3" data-testid={'compliance-case-file-' + caseId}>
          <p className="font-manrope text-[10px] text-slate-400 mb-2">Case file opened {file.opened_at ? new Date(file.opened_at).toLocaleString() : ''} by {file.opened_by}</p>
          <Section title="Buyer"><Row k="Legal name" v={file.buyer.legal_name} /><Row k="Contact" v={file.buyer.contact} /><Row k="Email" v={file.buyer.email} /><Row k="Incorporated" v={file.buyer.country_of_incorporation} /><Row k="HQ" v={file.buyer.hq_country} /><Row k="VAT" v={file.buyer.vat_number} /></Section>
          <Section title="Ultimate parent"><Row k="Name" v={file.ultimate_parent.name} /><Row k="HQ" v={file.ultimate_parent.hq_country} /></Section>
          <Section title="Destination"><Row k="Delivery" v={file.destination.delivery} /><Row k="Installation" v={file.destination.installation_country} /></Section>
          <Section title="End user"><Row k="Buyer is end user" v={file.end_user.buyer_is_end_user} /><Row k="Name" v={file.end_user.name} /><Row k="Country" v={file.end_user.country} /></Section>
          <Section title="Use"><Row k="Intended use" v={file.intended_use} /><Row k="Re-export / resale" v={file.reexport_intended} /></Section>
          <Section title="Product"><Row k="Requested" v={file.product.requested} /><Row k="Class" v={file.product.product_class} /><Row k="ECCN" v={file.product.eccn} /><Row k="Offer" v={file.product.offer_id ? file.product.offer_id.substring(0, 8) : ''} /></Section>
          <Section title="Acknowledgement & screening"><Row k="Acknowledged" v={file.acknowledgement.acknowledged} /><Row k="At" v={file.acknowledgement.acknowledged_at ? new Date(file.acknowledgement.acknowledged_at).toLocaleString() : ''} /><Row k="Screening" v={file.screening.status} /></Section>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 p-3 mb-3 grid grid-cols-2 gap-x-4 gap-y-1" data-testid={'compliance-data-' + caseId}>
          {FIELD_LABELS.map(function(pair) { return <Row key={pair[0]} k={pair[1]} v={c[pair[0]]} />; })}
          {c.acknowledged_at && <div className="col-span-2 font-manrope text-[10px] text-slate-400 mt-1">Acknowledged {new Date(c.acknowledged_at).toLocaleString()}</div>}
        </div>
      )}

      {(data.compliance_conditions || data.compliance_licence_reference) && (
        <div className="font-manrope text-xs text-slate-700 mb-3">
          {data.compliance_conditions && <p><span className="text-slate-500">Conditions: </span>{data.compliance_conditions}</p>}
          {data.compliance_licence_reference && <p><span className="text-slate-500">Licence ref: </span>{data.compliance_licence_reference}</p>}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
        <select value={decision} onChange={function(e){ setDecision(e.target.value); }} className="px-3 py-2 rounded-lg border border-slate-200 font-manrope text-xs bg-white" data-testid={'compliance-decision-select-' + caseId}>
          <option value="">Set compliance status…</option>
          {COMPLIANCE_STATUSES.filter(function(s){ return s !== 'NOT_REQUIRED' && s !== status; }).map(function(s){ return <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>; })}
        </select>
        <input value={eccn} onChange={function(e){ setEccn(e.target.value); }} placeholder="ECCN (from supplier data only)" className="px-3 py-2 rounded-lg border border-slate-200 font-manrope text-xs bg-white" data-testid={'compliance-eccn-' + caseId} />
        <input value={licenceRef} onChange={function(e){ setLicenceRef(e.target.value); }} placeholder="Licence / authorisation reference" className="px-3 py-2 rounded-lg border border-slate-200 font-manrope text-xs bg-white" data-testid={'compliance-licence-' + caseId} />
      </div>
      {decision === 'CLEARED_WITH_CONDITIONS' && (
        <textarea value={conditions} onChange={function(e){ setConditions(e.target.value); }} placeholder="State the conditions of clearance" className="w-full px-3 py-2 rounded-lg border border-slate-200 font-manrope text-xs bg-white mb-2 h-16 resize-none" data-testid={'compliance-conditions-' + caseId} />
      )}
      <div className="flex flex-col md:flex-row gap-2">
        <input value={note} onChange={function(e){ setNote(e.target.value); }} placeholder={DECISION_STATUSES.indexOf(decision) >= 0 ? 'Reviewer note (required)' : 'Reviewer note'} className="flex-1 px-3 py-2 rounded-lg border border-slate-200 font-manrope text-xs bg-white" data-testid={'compliance-note-' + caseId} />
        <button onClick={recordDecision} disabled={busy || !decision} className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 bg-white font-manrope text-xs font-semibold text-slate-700 hover:border-[#0096D6] disabled:opacity-50" data-testid={'compliance-record-' + caseId}>
          <Save className="w-3.5 h-3.5" /> Record Decision
        </button>
      </div>
      {error && <p className="font-manrope text-xs text-red-600 mt-2" data-testid={'compliance-error-' + caseId}>{error}</p>}

      {log.length > 0 && (
        <div className="mt-3">
          <p className="font-manrope text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Compliance log</p>
          <div className="space-y-1">
            {log.slice().reverse().map(function(entry, i) {
              return <div key={i} className="flex flex-wrap items-center gap-2 font-manrope text-[11px]">
                <span className="text-slate-400">{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ''}</span>
                <ComplianceBadge status={entry.status} testId={'compliance-log-' + caseId + '-' + i} />
                <span className="text-slate-500">{entry.by}</span>
                {entry.note && <span className="text-slate-700">— {entry.note}</span>}
              </div>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}
